import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Video } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const STATUS_LABELS = { awaiting_candidate: "Awaiting time selection", scheduled: "Confirmed", reschedule_requested: "Reschedule requested", awaiting_reconfirmation: "Awaiting reconfirmation", completed: "Completed", no_show: "No-show", declined: "Declined", cancelled: "Cancelled" };
const PILL_STYLES = { awaiting_candidate: "bg-amber-50 text-amber-700", scheduled: "bg-green-50 text-green-700", reschedule_requested: "bg-orange-50 text-orange-700", awaiting_reconfirmation: "bg-amber-50 text-amber-700", completed: "bg-blue-50 text-blue-700", no_show: "bg-red-50 text-red-700", declined: "bg-zinc-100 text-zinc-500", cancelled: "bg-zinc-100 text-zinc-400" };

function StatusPill({ s }) {
  return (
    <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${PILL_STYLES[s] || "bg-zinc-100 text-zinc-500"}`}
      data-testid={`civ-status-${s}`}>
      {STATUS_LABELS[s] || s}
    </span>
  );
}

export default function CandidateInterviews() {
  const [items, setItems] = useState([]);
  const [slots, setSlots] = useState({});
  const [picking, setPicking] = useState("");
  const [reason, setReason] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get("/candidate/interviews").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openPicker = async (iv) => {
    setPicking(iv.id);
    try {
      const r = await api.get(`/candidate/interviews/${iv.id}/slots`);
      setSlots((s) => ({ ...s, [iv.id]: r.data.slots }));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const choose = async (iv, at) => {
    try {
      await api.post(`/candidate/interviews/${iv.id}/choose`, { at });
      toast.success("Interview booked, confirmation emailed.");
      setPicking("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const act = async (iv, action, body = {}) => {
    try {
      await api.post(`/candidate/interviews/${iv.id}/${action}`, body);
      toast.success(action === "decline" ? "Interview declined." : action === "recording-consent" ? "Preference saved." : "Reschedule requested, the employer has been notified.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) {
    return (
      <div className="py-16 text-center" data-testid="civ-loading">
        <div className="h-8 w-8 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  const active = items.filter((i) => ["awaiting_candidate", "scheduled", "reschedule_requested", "awaiting_reconfirmation"].includes(i.status));
  const past = items.filter((i) => ["completed", "no_show", "declined", "cancelled"].includes(i.status));

  return (
    <div className="space-y-6" data-testid="civ-tab">
      {items.length === 0 && (
        <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center" data-testid="civ-empty">
          <Calendar className="h-8 w-8 text-zinc-300 mx-auto mb-2" />
          <p className="font-display text-xl font-bold text-zinc-900">No interviews yet</p>
          <p className="mt-2 text-sm text-zinc-500">When an employer invites you to interview, it will appear here.</p>
        </div>
      )}

      {active.map((iv) => (
        <div key={iv.id} className="bg-white border border-zinc-200 rounded-lg p-5 sm:p-6" data-testid={`civ-card-${iv.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-zinc-950">{iv.job_title} <span className="text-zinc-500 font-normal">· {iv.company_name}</span></p>
                <StatusPill s={iv.status} />
              </div>
              <p className="text-sm text-zinc-500 mt-1">
                {iv.when_str ? `${iv.when_str} · ${iv.duration_minutes} min · ${iv.provider_label}` : `${iv.duration_minutes} min · ${iv.provider_label}`}
              </p>
              {iv.location ? <p className="text-xs text-zinc-400 mt-0.5">{iv.location}</p> : null}
              {iv.interviewers?.length > 0 && (
                <p className="text-xs text-zinc-400 mt-0.5">You will meet: {iv.interviewers.map((i) => i.name).join(", ")}</p>
              )}
            </div>
            <Link to={`/interviews/${iv.id}`} data-testid={`civ-open-${iv.id}`}
              className="shrink-0 text-xs font-semibold border border-zinc-300 rounded-md px-3 py-1.5 hover:border-zinc-500 transition-colors">
              Details
            </Link>
          </div>

          {iv.status === "awaiting_candidate" && (
            <div className="mt-4">
              <button onClick={() => (picking === iv.id ? setPicking("") : openPicker(iv))} data-testid={`civ-pick-${iv.id}`}
                className="h-10 px-5 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors">
                Choose a time
              </button>
              {picking === iv.id && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto" data-testid={`civ-slots-${iv.id}`}>
                  {(slots[iv.id] || []).length === 0 ? (
                    <p className="text-sm text-zinc-500 col-span-2">No free slots in the employer's current availability. Try again soon or request a different time below.</p>
                  ) : (
                    (slots[iv.id] || []).map((s) => (
                      <button key={s.at} onClick={() => choose(iv, s.at)} data-testid={`civ-slot-${s.at}`}
                        className="h-10 px-3 text-sm border border-zinc-300 rounded-md hover:border-zinc-950 hover:bg-zinc-50 transition-colors text-left">
                        {s.label}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {iv.status === "awaiting_reconfirmation" && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-4" data-testid={`civ-reconfirm-${iv.id}`}>
              <p className="text-sm text-amber-900">The employer updated this interview. Please check the details above and reconfirm, or ask for a different time.</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <button onClick={() => act(iv, "reconfirm")} data-testid={`civ-reconfirm-yes-${iv.id}`}
                  className="h-9 px-4 text-xs font-semibold bg-zinc-950 text-white rounded-md hover:bg-zinc-800 transition-colors">Reconfirm interview</button>
              </div>
            </div>
          )}

          {iv.status === "scheduled" && iv.link && iv.link.startsWith("https://") && (
            <a href={iv.link} target="_blank" rel="noreferrer" data-testid={`civ-join-${iv.id}`}
              className="mt-4 inline-flex items-center gap-2 h-10 px-5 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors">
              <Video className="h-4 w-4" /> Join interview
            </a>
          )}

          {iv.recording?.requested && iv.recording?.consent === null && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid={`civ-recording-${iv.id}`}>
              <p className="text-sm text-amber-900">The employer would like to record this interview. Recording only happens with your consent.</p>
              <div className="flex gap-2 mt-2">
                <button onClick={() => act(iv, "recording-consent", { consent: true })} data-testid={`civ-consent-yes-${iv.id}`}
                  className="h-8 px-3 text-xs font-semibold bg-zinc-950 text-white rounded-md hover:bg-zinc-800 transition-colors">I consent</button>
                <button onClick={() => act(iv, "recording-consent", { consent: false })} data-testid={`civ-consent-no-${iv.id}`}
                  className="h-8 px-3 text-xs font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors">No thanks</button>
              </div>
            </div>
          )}

          {["scheduled", "awaiting_candidate", "awaiting_reconfirmation"].includes(iv.status) && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <input value={reason[iv.id] || ""} onChange={(e) => setReason((r) => ({ ...r, [iv.id]: e.target.value }))}
                placeholder="Reason (optional)" data-testid={`civ-reason-${iv.id}`}
                className="h-9 px-3 border border-zinc-300 rounded-md text-sm flex-1 min-w-[160px]" />
              {["scheduled", "awaiting_reconfirmation"].includes(iv.status) && (
                <button onClick={() => act(iv, "reschedule-request", { reason: reason[iv.id] || "" })} data-testid={`civ-resched-${iv.id}`}
                  className="h-9 px-3 text-xs font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors">
                  Ask for a different time
                </button>
              )}
              <button onClick={() => { if (window.confirm("Decline this interview?")) act(iv, "decline", { reason: reason[iv.id] || "" }); }}
                data-testid={`civ-decline-${iv.id}`}
                className="h-9 px-3 text-xs font-semibold border border-zinc-300 rounded-md hover:border-red-400 hover:text-red-600 transition-colors">
                Decline
              </button>
            </div>
          )}

          {iv.status === "reschedule_requested" && (
            <p className="mt-4 text-sm text-orange-700 bg-orange-50 rounded-md px-3 py-2" data-testid={`civ-resched-note-${iv.id}`}>
              You asked for a different time, the employer will update the interview and email you.
            </p>
          )}
        </div>
      ))}

      {past.length > 0 && (
        <section>
          <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Past</h3>
          <div className="space-y-2">
            {past.map((iv) => (
              <div key={iv.id} className="bg-white border border-zinc-200 rounded-md px-4 py-3 text-sm text-zinc-600 flex items-center justify-between gap-2 flex-wrap" data-testid={`civ-past-${iv.id}`}>
                <span>{iv.job_title} · {iv.company_name} · {iv.when_str || "time was not set"}</span>
                <StatusPill s={iv.status} />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
